import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IMacaddrModel } from "../../webapi/service/getLicences";
import MemoText from "./MemoText";
import EditButton from "../common/EditButton";
import SaveButton from "../common/SaveButton";
import CancelButton from "../common/CancelButton";

/**
 * 最大メモ長
 */
export const MaxMemoLength = 120;

export interface IProps {
  /**
   * シリアル情報
   */
  macaddrs: IMacaddrModel[];
  /**
   * シリアル情報更新
   */
  onUpdateMacaddr: (macaddr: string, memo: string) => void;
}

interface MacaddrEditInfo {
  /**
   * シリアル番号
   */
  macaddr: string;
  /**
   * メモ
   */
  memo: string;
  /**
   * 編集中の場合true
   */
  edit: boolean;
  /**
   * undo文字列
   */
  undo: string;
}

/**
 * シリアルリストElement
 * @param props - シリアルリストパラメータ
 * @returns シリアルリストElement
 */
export default function MacaddrList(props: IProps) {
  const { t } = useTranslation();
  const [editInfos, setEditInfos] = useState<MacaddrEditInfo[]>([]);

  useEffect(() => {
    const editInfos = props.macaddrs.map((macaddr) => {
      return {
        macaddr: macaddr.macaddr,
        memo: macaddr.memo,
        edit: false,
        undo: "",
      };
    });
    setEditInfos(editInfos);
  }, [props.macaddrs]);

  function handleClickEdit(macaddr: string) {
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.macaddr === macaddr;
    });
    if (find) {
      find.edit = true;
      find.undo = find.memo;
      setEditInfos(slice);
    }
  }

  function handleClickSave(macaddr: string) {
    const find = editInfos.find((info) => {
      return info.macaddr === macaddr;
    });
    if (find) {
      props.onUpdateMacaddr(macaddr, find.memo);
    }
  }

  function handleClickCancel(macaddr: string) {
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.macaddr === macaddr;
    });
    if (find) {
      find.edit = false;
      find.memo = find.undo;
      find.undo = "";
      setEditInfos(slice);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.macaddr === id;
    });
    if (find) {
      find.memo = value;
      setEditInfos(slice);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      const { id, value } = e.target as HTMLInputElement;
      props.onUpdateMacaddr(id, value);
    }
  }

  const body = editInfos.map((info, i) => {
    const { macaddr, memo, edit } = info;
    const memoText = edit ? (
      <Form.Control
        style={{ width: "290px" }}
        id={macaddr}
        defaultValue={memo}
        maxLength={MaxMemoLength}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    ) : (
      <MemoText text={memo} />
    );
    const editButton = edit ? (
      <div style={{ display: "flex" }}>
        <SaveButton onClick={() => handleClickSave(macaddr)} />
        <CancelButton onClick={() => handleClickCancel(macaddr)} />
      </div>
    ) : (
      <EditButton onClick={() => handleClickEdit(macaddr)} />
    );
    return (
      <tr key={i}>
        <td>{info.macaddr}</td>
        <td style={{ width: "250px" }}>{memoText}</td>
        <td style={{ width: "40px" }}>{editButton}</td>
      </tr>
    );
  });

  return (
    <table>
      <thead>
        <tr>
          <th>{t("license_macaddr_list")}</th>
          <th colSpan={2}>{t("license_macaddr_memo")}</th>
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );
}
